import { isEmpty } from '../utils/global-utils'
import cachios from 'cachios'
import User from '../models/User'
import Token from '../models/Token'
import * as mutationTypes from '../store/mutation-types'
import emitter from '../utils/global-emitter'
import i18n from '../utils/i18n'
import * as actionTypes from '../store/action-types'
import Cookies from 'js-cookie'

class LoginManager {
    constructor () {
        if (!LoginManager.instance) {
            LoginManager.instance = this
        }

        this._apiKey = ''
        this._additionalInfoDomain = ''
        this._checkAccount = false
        this._cookiesDomain = ''
        this._isAdditionalInfoRequired = true
        this._isInitialized = false
        this._isRefreshTokenEnabled = true
        this._kasConfiguration = null
        this._kasConfigurationSettings = null
        this._kasConfigurationSettingsForms = null
        this._kasConfigurationSettingsPages = null
        this._loginMode = 'kas'
        this._rawUserInfos = null
        this._redirectUrl = ''
        this._refreshUserTokenTimeout = null
        this._retryCounter = {
            accounts: 0,
            checkAccount: 0,
            configuration: 0,
            configurationSettings: 0,
            me: 0,
            refreshToken: 0,
            token: 0
        }
        this._serverSideStatus = ''
        this._store = null
        this._tokenRefreshSafetyDelay = 180
        this._userWebUrl = ''
        this._userInfos = null
        this._userInfosAttributes = 'lastName,firstName,email,birthDate,phoneNumber'
        this._userToken = null

        const retryLatency = 15

        this.clearTokens = () => {
            this.clearStoredInfos()
            localStorage.removeItem('kasConfiguration')
            delete cachios.axiosInstance.defaults.headers.common.Authorization
            this.logoutAction()
        }

        this.checkStoredInfos = () => {
            // console.log('checkStoredInfos') // #tokens-debugging
            this.getLocalStorageKasConfiguration()
            this.getLocalStorageKasConfigurationSettings()
            this.getKasConfigurationSettingsFormsFromCookies()
            this.getKasConfigurationSettingsPagesFromCookies()
            this.getUserTokenFromCookies()
            this.getUserInfosFromCookies()
        }

        this.checkTokensValidity = () => {
            // console.log('checkTokensValidity') // #tokens-debugging
            const userTokenState = this.getUserTokenState()

            if (userTokenState === 'valid') {
                // console.log('EMIT validUserTokenFound') // #tokens-debugging
                emitter.emit('validUserTokenFound')
            } else if (userTokenState === 'expired') {
                // console.log('EMIT userTokenExpired') // #tokens-debugging
                emitter.emit('userTokenExpired')
            } else {
                // console.log('EMIT tokenExistenceChecked') // #tokens-debugging
                emitter.emit('tokenExistenceChecked')
            }
        }

        this.checkServerSideStatus = () => {
            const userTokenCookie = Cookies.get('userToken')
            if (this.serverSideStatus !== '') {
                if (this.serverSideStatus === 'LOGGED_OUT' && !isEmpty(userTokenCookie)) {
                    const userStatusSyncRetry = this.getLocalStorageItem('userStatusSyncRetry')

                    if (isEmpty(userStatusSyncRetry)) {
                        if (typeof cachios.axiosInstance.defaults.headers.common.Authorization === 'undefined') {
                            this.applyUserTokenToAxios()
                        }
                        this.getUserInfosAction({
                            checkAuthenticationStatus: true,
                            callback: (data) => {
                                this.setUserInfosCookie(data)
                                this.applyUserTokenToAxios()
                                emitter.emit('clientSideUserTokenValidated')
                            }
                        })
                    } else {
                        localStorage.removeItem('userStatusSyncRetry')
                        this.store.dispatch('login/' + actionTypes.LOGOUT, {
                            autoLogOut: true
                        })
                    }
                } else if (this.serverSideStatus === 'LOGGED_IN' && isEmpty(userTokenCookie)) {
                    this.store.dispatch('login/' + actionTypes.LOGOUT, {
                        autoLogOut: true
                    })
                } else {
                    localStorage.removeItem('userStatusSyncRetry')
                    if (this.serverSideStatus === 'LOGGED_IN') {
                        this.applyUserTokenToAxios()
                    }
                }
            }
        }

        this.isAuthenticated = () => {
            return this.store.state.login.authenticated
        }

        this.getUserTokenState = () => {
            let userTokenState = 'none'
            if (!isEmpty(this.userToken)) {
                if (this.loginMode === 'legacy' || this.userToken.getRemainingTime() > 0) {
                    userTokenState = 'valid'
                } else {
                    userTokenState = 'expired'
                }
            }
            return userTokenState
        }

        this.checkUserInfos = () => {
            // console.log('checkUserInfos', this.userInfos, this.getUserTokenState()) // #tokens-debugging
            if (isEmpty(this.userInfos) && this.getUserTokenState() === 'valid') {
                if (this.checkAccount) {
                    // console.log('CALL checkAccountAction FROM checkUserInfos') // #tokens-debugging
                    this.checkAccountAction()
                } else {
                    this.getUserInfosAction()
                }
            }
        }

        this.clearStoredInfos = () => {
            // console.log('clearStoredInfos') // #tokens-debugging
            Cookies.remove('userToken', {
                domain: this.cookiesDomain,
                path: '/'
            })
            this.store.commit(`login/${mutationTypes.SET_USER_TOKEN}`, {})
            this.userToken = null
            Cookies.remove('userAccountCompleted', {
                domain: this.cookiesDomain,
                path: '/'
            })
            Cookies.remove('userInfos', {
                domain: this.additionalInfoDomain,
                path: '/'
            })
            this.store.commit(`login/${mutationTypes.SET_USER_INFOS}`, {})
            this.store.commit(`login/${mutationTypes.SET_RAW_USER_INFOS}`, {})
            this.userInfos = null
            this.rawUserInfos = null
            localStorage.removeItem('kasConfiguration')
            this.kasConfiguration = null
            localStorage.removeItem('kasConfigurationSettings')
            this.kasConfigurationSettings = null
            this.store.commit(`login/${mutationTypes.SET_KAS_CONFIGURATION_SETTINGS}`, {})
            this.store.commit(`login/${mutationTypes.SET_AUTHENTICATED}`, false)

            delete cachios.axiosInstance.defaults.headers.common.Authorization
        }

        this.getLocalStorageItem = (key) => {
            const item = localStorage.getItem(key)
            if (item) {
                return JSON.parse(item)
            }
            return null
        }

        this.setLocalStorageItem = (key, value) => {
            localStorage.setItem(key, JSON.stringify(value))
        }

        this.scheduleRefreshUserTokenCall = () => {
            // console.log('scheduleRefreshUserTokenCall') // #tokens-debugging
            const remainingTime = this.userToken.getRemainingTime('user')

            if (!isNaN(remainingTime) && remainingTime > this.tokenRefreshSafetyDelay) {
                // If there is more than 10 sec remaining, set a timeout to defers refreshTokenAction call
                let delay = Math.floor((remainingTime - this.tokenRefreshSafetyDelay) * 1000)
                if (process.env.VUE_APP_SIMULATE_DURABLE_LOCAL_STORAGE) {
                    delay = 7200000 // 2h
                }
                // console.log('DELAY BEFORE REFRESHING USER TOKEN : ' + delay / 1000 + 's') // #tokens-debugging
                clearTimeout(this._refreshUserTokenTimeout)
                // console.log('scheduleRefreshUserTokenCall >>> SET TIMEOUT FOR REFRESHING TOKEN') // #tokens-debugging
                this._refreshUserTokenTimeout = setTimeout(() => {
                    // console.log('scheduleRefreshUserTokenCall >>> TIMEOUT FINISHED >>> REFRESHING TOKEN') // #tokens-debugging
                    if (this.isRefreshTokenEnabled) {
                        this.refreshTokenAction()
                    }
                }, delay)
            } else if (this.isRefreshTokenEnabled && !isNaN(remainingTime) && remainingTime > 0) {
                // If there is less than 30 sec remaining, call directly refreshTokenAction
                // console.log('scheduleRefreshUserTokenCall >>> REFRESHING TOKEN') // #tokens-debugging
                this.refreshTokenAction()
            } else {
                // console.log('userToken has expired and can not be refreshed...') // #tokens-debugging
                emitter.emit('userTokenExpired')
            }
        }

        /**
         * Set userToken as default token and applies it as default cachios authorization token
         */
        this.applyUserTokenToAxios = () => {
            // console.log('applyUserTokenToAxios') // #tokens-debugging
            // console.log('applyUserTokenToAxios', this.userToken.accessToken.substr(this.userToken.accessToken.length - 10)) // #tokens-debugging
            if (!isEmpty(this.userToken)) {
                // console.log('Apply userToken as cachios default token')
                cachios.axiosInstance.defaults.headers.common.Authorization = this.userToken.accessToken
                // console.log('APPLIED USER TOKEN :')
                // console.log(this.userToken.accessToken)
                this.scheduleRefreshUserTokenCall()
            } else {
                console.log('User token is not set yet, can not use it as defaut token...')
            }
        }

        /**
         * Get kasConfiguration from localStorage and set LoginManager kasConfiguration property
         * @returns {any|null}
         */
        this.getLocalStorageKasConfiguration = () => {
            const kasConfiguration = this.getLocalStorageItem('kasConfiguration')
            if (!isEmpty(kasConfiguration)) {
                // console.log(`Set LoginManager kasConfiguration from the localStorage`)
                this._kasConfiguration = kasConfiguration
                return kasConfiguration
            }
            return null
        }

        /**
         * Add provided kasConfiguration to localStorage and set LoginManager kasConfiguration property
         * @param value
         */
        this.setLocalStorageKasConfiguration = (value) => {
            if (!isEmpty(value)) {
                // console.log(`Set LoginManager kasConfiguration before storing it in the localStorage`)
                this._kasConfiguration = value
            }
            this.setLocalStorageItem('kasConfiguration', value)
        }

        /**
         * Get kasConfigurationSettings from localStorage and set LoginManager kasConfigurationSettings property
         * @returns {any|null}
         */
        this.getLocalStorageKasConfigurationSettings = () => {
            const kasConfigurationSettings = this.getLocalStorageItem('kasConfigurationSettings')
            if (!isEmpty(kasConfigurationSettings)) {
                // console.log(`Set LoginManager kasConfigurationSettings from the localStorage`)
                this._kasConfigurationSettings = kasConfigurationSettings
                return kasConfigurationSettings
            }
            return null
        }

        /**
         * Add provided kasConfigurationSettings to localStorage and set LoginManager kasConfigurationSettings property
         * @param value
         */
        this.setLocalStorageKasConfigurationSettings = (value) => {
            if (!isEmpty(value)) {
                // console.log(`Set LoginManager kasConfigurationSettings before storing it in the localStorage`)
                this._kasConfigurationSettings = value
            }
            this.setLocalStorageItem('kasConfigurationSettings', value)
        }

        /**
         * Get kasConfigurationSettingsForms from cookies and set LoginManager kasConfigurationSettingsForms property
         * @returns {any|null}
         */
        this.getKasConfigurationSettingsFormsFromCookies = () => {
            let kasConfigurationSettingsForms = Cookies.get('kasConfigurationSettingsForms')

            if (!isEmpty(kasConfigurationSettingsForms)) {
                try {
                    kasConfigurationSettingsForms = JSON.parse(decodeURIComponent(kasConfigurationSettingsForms))
                } catch (error) {
                    console.warn('Error while decoding kasConfigurationSettingsForms cookie value, using undecoded raw value.', error)

                    try {
                        kasConfigurationSettingsForms = JSON.parse(kasConfigurationSettingsForms)
                    } catch (parseError) {
                        console.error('Error parsing kasConfigurationSettingsForms cookie raw value.', parseError)

                        return null
                    }
                }

                if (!isEmpty(kasConfigurationSettingsForms)) {
                    this._kasConfigurationSettingsForms = kasConfigurationSettingsForms
                }

                return kasConfigurationSettingsForms
            }

            return null
        }

        /**
         * Add provided kasConfigurationSettingsForms to cookies and set LoginManager kasConfigurationSettingsForms property
         * @param value
         */
        this.setKasConfigurationSettingsFormsCookie = (value) => {
            if (!isEmpty(value)) {
                this._kasConfigurationSettingsForms = value
            }

            Cookies.set('kasConfigurationSettingsForms', JSON.stringify(value), {
                domain: this.cookiesDomain,
                path: '/'
            })
        }

        /**
         * Get kasConfigurationSettingsPages from cookies and set LoginManager kasConfigurationSettingsPages property
         * @returns {any|null}
         */
        this.getKasConfigurationSettingsPagesFromCookies = () => {
            let kasConfigurationSettingsPages = Cookies.get('kasConfigurationSettingsPages')

            if (!isEmpty(kasConfigurationSettingsPages)) {
                try {
                    kasConfigurationSettingsPages = JSON.parse(decodeURIComponent(kasConfigurationSettingsPages))
                } catch (error) {
                    console.warn('Error while decoding kasConfigurationSettingsPages cookie value, using undecoded raw value.', error)

                    try {
                        kasConfigurationSettingsPages = JSON.parse(kasConfigurationSettingsPages)
                    } catch (parseError) {
                        console.error('Error parsing kasConfigurationSettingsPages cookie raw value.', parseError)

                        return null
                    }
                }

                if (!isEmpty(kasConfigurationSettingsPages)) {
                    this._kasConfigurationSettingsPages = kasConfigurationSettingsPages
                }

                return kasConfigurationSettingsPages
            }

            return null
        }

        /**
         * Add provided kasConfigurationSettingsPages to cookies and set LoginManager kasConfigurationSettingsPages property
         * @param value
         */
        this.setKasConfigurationSettingsPagesCookie = (value) => {
            if (!isEmpty(value)) {
                this._kasConfigurationSettingsPages = value
            }

            Cookies.set('kasConfigurationSettingsPages', JSON.stringify(value), {
                domain: this.cookiesDomain,
                path: '/'
            })
        }

        /**
         * Get userToken from cookies and set LoginManager userToken property with the accessToken key's value
         * @returns {any|null}
         */
        this.getUserTokenFromCookies = () => {
            let userToken = Cookies.get('userToken')
            if (!isEmpty(userToken)) {
                userToken = JSON.parse(decodeURIComponent(userToken))
                if (!isEmpty(userToken.accessToken)) {
                    // console.log(`Set LoginManager userToken from cookies`)
                    this._userToken = new Token(userToken)
                } else if (!isEmpty(userToken.token) && !isEmpty(userToken.token.accessToken)) {
                    // console.log(`Set LoginManager userToken from cookies`)
                    this._userToken = new Token(userToken.token)
                } else {
                    // console.log(`Found an user token, but it seems that it has no accessToken key...`)
                }
                // console.log('userToken', this.userToken)
                return userToken
            }
            // console.log('userToken NULL')
            return null
        }

        /**
         * Add provided userToken to cookies and set LoginManager userToken property with the accessToken key's value
         * @param value
         */
        this.setUserTokenCookie = (value) => {
            // console.log('setUserTokenCookie')
            if (!isEmpty(value.accessToken)) {
                this._userToken = new Token(value)
            } else if (!isEmpty(value.token) && !isEmpty(value.token.accessToken)) {
                this._userToken = new Token(value.token)
            } else {
                console.log('It seems that the provided item has no accessToken key...')
            }
            Cookies.set('userToken', JSON.stringify(value), {
                expires: this.userToken.getExpirationDate(),
                domain: this.cookiesDomain,
                path: '/'
            })
        }

        /**
         * Get userInfos from cookies and set LoginManager userInfos property
         * @returns {any|null}
         */
        this.getUserInfosFromCookies = () => {
            let userInfos = Cookies.get('userInfos')
            if (!isEmpty(userInfos)) {
                userInfos = JSON.parse(decodeURIComponent(userInfos))
                // console.log(`Set LoginManager userInfos from cookies`)
                this.userInfos = this.normalizeUserInfos(userInfos)
                this.rawUserInfos = userInfos
                return userInfos
            }
            return null
        }

        /**
         * Add provided userInfos to cookies and set LoginManager userInfos property
         * @param value
         */
        this.setUserInfosCookie = (value) => {
            if (!isEmpty(value)) {
                // console.log(`Set LoginManager userInfos before storing it in cookies`)
                this.userInfos = this.normalizeUserInfos(value)
                this.rawUserInfos = value
            }
            Cookies.set('userInfos', JSON.stringify(value), {
                expires: this.userToken.getExpirationDate(),
                domain: !isEmpty(this.additionalInfoDomain) ? this.additionalInfoDomain : this.cookiesDomain,
                path: '/'
            })
        }

        /**
         * Update userInfos cookie expiration date
         */
        this.updateUserInfosCookieExpiration = () => {
            let userInfos = Cookies.get('userInfos')
            if (!isEmpty(userInfos)) {
                userInfos = decodeURIComponent(userInfos)
                Cookies.set('userInfos', userInfos, {
                    expires: this.userToken.getExpirationDate(),
                    domain: !isEmpty(this.additionalInfoDomain) ? this.additionalInfoDomain : this.cookiesDomain,
                    path: '/'
                })
            }
        }

        this.normalizeUserInfos = (value) => {
            let user
            if (this.loginMode === 'kas') {
                user = new User(value.attributes.email)
                user.firstName = value.attributes.firstName ? value.attributes.firstName.ucFirst() : ''
                user.lastName = value.attributes.lastName ? value.attributes.lastName.ucFirst() : ''
            } else {
                user = new User(value.email)
                user.address = value.address
                user.addressDetail = value.addressDetail
                user.birthdate = value.birthdate
                user.city = value.city
                user.firstName = value.firstname
                user.gender = value.gender
                user.id = value.id
                user.image = value.imageUrl
                user.internationalPhone = value.internationalPhone
                user.lastName = value.lastname
                user.nationality = value.nationality
                user.phone = value.phone
                user.postalCode = value.postalCode
            }

            user.externalUserId = typeof value.externalUserId !== 'undefined' ? value.externalUserId : ''
            user.technicalId = typeof value.technicalId !== 'undefined' ? value.technicalId : ''

            return user
        }

        this.responseInterceptor = (error) => {
            if (error.config && error.config.skipInterceptor) {
                return Promise.reject(error)
            }

            if (error.response && error.response.status === 401) {
                // // console.log('REPONSE INTERCEPTOR 401 >>> ' + this.whichTokenIsActive()) // #tokens-debugging
                // console.log('REPONSE INTERCEPTOR 401 >>> user', error) // #tokens-debugging
                //                 if (this.whichTokenIsActive() === 'user') {
                // CHECK IF TOKEN IS REALLY EXPIRED AND REFRESH TOKEN IF IT IS
                // if (this.userToken.getRemainingTime(this.whichTokenIsActive()) > 0) {
                if (!isEmpty(this.userToken) && this.userToken.getRemainingTime('user') > 0) {
                    // console.log('INTERCEPTOR >>> USER TOKEN IS SUPPOSED TO BE STILL VALID, SO CHECK IT !') // #tokens-debugging
                    // Exclude token validity for check-account endpoint as it is always get a 401 when called just after an account creation
                    if (error.request.responseURL.indexOf('check-account') === -1) {
                        this.getUserInfosAction({
                            checkAuthenticationStatus: true
                        })
                    }
                } else if (!isEmpty(this.userToken)) {
                    // console.log('INTERCEPTOR >>> USER TOKEN EXPIRED >>> EMIT userTokenExpired') // #tokens-debugging
                    emitter.emit('userTokenExpired')
                } else {
                    console.log("You got a 401 error, it seems you dit not provided a user token, so either you should have provided it with one, or the webservice doesn't require a user token, in which case the problem is server or configuration side.")
                }
            }
            // Do something with request error
            return Promise.reject(error)
        }

        this.convertRoute = (route) => {
            if (typeof route === 'string') {
                return route
            }
            return {
                name: route.name,
                params: route.params,
                path: route.path,
                query: route.query
            }
        }

        this.startLogInProcess = (redirectTo) => {
            // console.log('startLogInProcess') // #tokens-debugging
            // console.log('redirectTo', redirectTo)
            if (isEmpty(redirectTo)) {
                redirectTo = window.location.href
            }
            const convertedRedirectTo = this.convertRoute(redirectTo)
            this.setLocalStorageItem('redirectTo', this.convertRoute(convertedRedirectTo))
            // console.log('convertedRedirectTo', convertedRedirectTo)

            if (this.loginMode === 'kas') {
                // console.log('goToKeyCloakLoginForm')
                if (isEmpty(this.kasConfiguration)) {
                    if (this.loginMode && this.loginMode === 'kas') {
                        this.getConfigurationSettingsAction()
                        this.getConfigurationAction({
                            callback: this.startLogInProcess.bind(this, convertedRedirectTo)
                        })
                    }
                } else {
                    // console.log('startLogInProcess', i18n.locale)
                    window.location.href = this.kasConfiguration.authorizationUrl.setUrlParameter('ui_locales', i18n?.global?.locale || i18n?.locale || 'fr')
                }
            } else {
                // console.log('showLoginModal')
                emitter.emit('showLoginForm')
            }
        }

        this.getUpdateAccountUrl = (redirectTo) => {
            if (!isEmpty(this.store.state.login.kasConfigurationSettings)) {
                return this.store.state.login.kasConfigurationSettings.pages.account.setUrlParameter('kc_locale', i18n?.global?.locale || i18n?.locale || 'fr')
            }
            return ''
        }

        this.getUpdatePasswordUrl = (redirectTo) => {
            if (!isEmpty(this.store.state.login.kasConfigurationSettings)) {
                return this.store.state.login.kasConfigurationSettings.pages.password.setUrlParameter('kc_locale', i18n?.global?.locale || i18n?.locale || 'fr')
            }
            return ''
        }

        this.onKasLogoutCallback = (params) => {
            // console.log('onKasLogoutCallback') // #tokens-debugging
            // console.log('onKasLogoutCallback', this.store.state.login.loginContext) // #tokens-debugging
            // console.log('onKasLogoutCallback', this.store.state.login.logoutSyncUrl) // #tokens-debugging
            this.clearStoredInfos()
            const event = new Event('kas-logged-out')
            if (!isEmpty(params) && !isEmpty(params.autoLogOut)) {
                emitter.emit('autoLoggedOut')
                document.body.dispatchEvent(event)
            }
            this.getConfigurationSettingsAction()
            this.getConfigurationAction({
                callback: () => {
                    // console.log('onKasLogoutCallback >>> getConfigurationAction >>> callback') // #tokens-debugging
                    // console.log('onKasLogoutCallback >>> getConfigurationAction >>> callback >>> this.store.state.login.logoutSyncUrl', this.store.state.login.logoutSyncUrl) // #tokens-debugging
                    if (this.store.state.login.loginContext === 'partial' && !isEmpty(this.store.state.login.logoutSyncUrl)) {
                        this.syncLogoutAction()
                    } else {
                        emitter.emit('loggedOut')
                        document.body.dispatchEvent(event)
                    }
                    if (typeof params !== 'undefined' && typeof params.callback !== 'undefined' && typeof params.callback === 'function') {
                        params.callback()
                    }
                }
            })
        }

        this.adaptTokenRefreshSafetyDelayIfNeeded = (userToken) => {
            if (userToken?.expiresIn && userToken?.expiresIn < this.tokenRefreshSafetyDelay) {
                this.isRefreshTokenEnabled = true

                if (userToken.expiresIn >= 300) {
                    this.tokenRefreshSafetyDelay = 180
                } else if (userToken.expiresIn > 120) {
                    this.tokenRefreshSafetyDelay = 60
                } else if (userToken.expiresIn > 60) {
                    this.tokenRefreshSafetyDelay = 30
                } else if (userToken.expiresIn > 30) {
                    this.tokenRefreshSafetyDelay = 10
                } else {
                    this.isRefreshTokenEnabled = false
                }

                console.log(`It seems you settled a token-refresh-safety-delay value inferior to the token lifetime (${userToken.expiresIn})..., trying to fix that by setting token-refresh-safety-delay value to ${this.tokenRefreshSafetyDelay} seconds`)
            }
        }

        this.getUserTokenSuccessHandler = (userToken) => {
            // console.log('getUserTokenSuccessHandler') // #tokens-debugging
            this.adaptTokenRefreshSafetyDelayIfNeeded(userToken)
            this.setUserTokenCookie(userToken)
            this.applyUserTokenToAxios()
            this.store.commit(`login/${mutationTypes.SET_USER_TOKEN}`, this.userToken)
            emitter.emit('userTokenLoaded')
            // console.log('getUserTokenSuccessHandler',  this.userToken.getGmtExpirationDate())
            // console.log('userToken', this.userToken)
            // debugger

            document.body.dispatchEvent(new CustomEvent('kas-user-token-loaded', {
                detail: {
                    kasConfiguration: userToken
                }
            }))

            if (this.checkAccount) {
                // console.log('CALL checkAccountAction FROM getUserTokenSuccessHandler') // #tokens-debugging
                this.checkAccountAction()
            } else {
                this.getUserInfosAction()
            }
        }

        /* =============================================================================================================
        =================================================== ACTIONS ====================================================
        ============================================================================================================= */
        this.checkAccountAction = (params) => {
            // console.log('CHECK_ACCOUNT') // #tokens-debugging
            const checkAccount = () => {
                // console.log('checkAccount', this.retryCounter.checkAccount)
                this.retryCounter.checkAccount++
                return cachios
                    .get(`${this.userWebUrl}/check-account`, {
                        force: true
                    })
                    .then(response => {
                        // console.log('CHECK_ACCOUNT >>> SUCCESS', response.data) // #tokens-debugging
                        Cookies.set('userAccountCompleted', 'yes', {
                            expires: this.userToken.getExpirationDate(),
                            domain: this.cookiesDomain,
                            path: '/'
                        })
                        localStorage.removeItem('firstCheckAccountTry')
                        this.retryCounter.checkAccount = 0
                        this.getUserInfosAction()
                    })
                    .catch(error => {
                        // console.log('CHECK_ACCOUNT >>> ERROR', error) // #tokens-debugging
                        if (
                            error.response.status === 403 &&
                            !isEmpty(error.response.data)
                        ) {
                            // console.log('CHECK_ACCOUNT >>> SET hasToRefreshToken IN LOCALSTORAGE') // #tokens-debugging
                            if (localStorage.getItem('firstCheckAccountTry')) {
                                localStorage.removeItem('firstCheckAccountTry')
                                this.logoutAction({
                                    autoLogOut: true,
                                    callback: () => {
                                        emitter.emit('autoLoggedOut')
                                        const event = new Event('kas-logged-out')
                                        document.body.dispatchEvent(event)
                                    }
                                })
                            } else {
                                localStorage.setItem('hasToRefreshToken', 1)
                                localStorage.setItem('firstCheckAccountTry', 1)
                                window.location.href = error.response.data
                            }
                        } else if (error.response.status === 401 && this.retryCounter.checkAccount < 5) {
                            // console.log('CALL checkAccountAction FROM checkAccount CATCH ERROR') // #tokens-debugging
                            this.getAccountsAction({
                                callback: () => {
                                    setTimeout(() => {
                                        this.checkAccountAction()
                                    })
                                }
                            })
                        } else {
                            emitter.emit('checkAccountError', error.response)
                            const event = new Event('kas-check-account-error', { detail: error.response })
                            document.body.dispatchEvent(event)
                        }
                    })
            }

            if (this.retryCounter.checkAccount > 0) {
                const timeout = 2 ** this.retryCounter.checkAccount * 1000
                // console.log('Timeout for checkAccount will be ' + timeout / 1000 + 's') // #tokens-debugging
                const promise = new Promise((resolve, reject) => {
                    setTimeout(() => {
                        checkAccount()
                    }, timeout)
                })
                return promise
            } else {
                return checkAccount()
            }
        }

        this.checkAuthorizationCodeAction = (params) => {
            // console.log('CHECK_AUTHORIZATION_CODE')
            const urlObject = new URL(window.location.href)
            const code = urlObject.searchParams.get('code')
            if (code) {
                this.getUserTokenAction({
                    code
                })
            }
        }

        this.checkStoredTokensAction = (params) => {
            // console.log('CHECK_STORED_TOKENS') // #tokens-debugging
            this.checkStoredInfos()
            if (localStorage.getItem('hasToRefreshToken') && localStorage.getItem('hasToRefreshToken')) {
                this.refreshTokenAction({
                    callback: this.checkStoredTokensAction
                })
            } else if (this.serverSideStatus !== '') {
                this.checkServerSideStatus()
            } else {
                this.checkTokensValidity()
                this.checkUserInfos()
            }
        }

        this.checkUserTokenValidityAction = (params) => {
            // console.log('CHECK_USER_TOKEN_VALIDITY') // #tokens-debugging
            return new Promise((resolve) => {
                const cachiosConfig = {
                    force: true,
                    skipInterceptor: true
                }

                if (params.accessToken) {
                    cachiosConfig.headers = {
                        Authorization: params.accessToken
                    }
                }

                cachios
                    .get(`${this.userWebUrl}/me?attributes=${this.userInfosAttributes}`, cachiosConfig)
                    .then(response => {
                        resolve({
                            isUserTokenValid: true,
                            userInfos: response.data
                        })
                    })
                    .catch(error => {
                        resolve({
                            isUserTokenValid: error.response.status === 401 ? false : undefined
                        })
                    })
            })
        }

        this.getAccountsAction = (params) => {
            // console.log('GET_ACCOUNTS') // #tokens-debugging

            return new Promise((resolve, reject) => {
                const getAccounts = () => {
                    // console.log('getAccounts', this.retryCounter.accounts)
                    this.retryCounter.accounts++
                    cachios
                        .post(`${this.userWebUrl}/accounts`, { force: true })
                        .then(response => {
                            // console.log('GET_ACCOUNTS >>> SUCCESS') // #tokens-debugging
                            this.retryCounter.accounts = 0

                            if (typeof params !== 'undefined' && typeof params.callback !== 'undefined' && typeof params.callback === 'function') {
                                // console.log('GET_ACCOUNTS >>> callback') // #tokens-debugging
                                params.callback(response.data)
                            } else {
                                this.getUserInfosAction()
                            }
                            resolve(response.data)
                        })
                        .catch(error => {
                            // console.log('GET_ACCOUNTS >>> ERROR', error) // #tokens-debugging
                            if (typeof params !== 'undefined' && typeof params.callback !== 'undefined' && typeof params.callback === 'function') {
                                // console.log('GET_ACCOUNTS >>> callback') // #tokens-debugging
                                params.callback({
                                    error
                                })
                            } else {
                                emitter.emit('getAccountsError', error.response)
                                const event = new Event('kas-get-accounts-error', { detail: error.response })
                                document.body.dispatchEvent(event)
                            }
                            reject(error)
                        })
                }

                if (this.retryCounter.accounts > 0) {
                    setTimeout(() => {
                        getAccounts()
                    }, retryLatency ** this.retryCounter.accounts)
                } else {
                    getAccounts()
                }
            })
        }

        this.unregisterAction = (params) => {
            // console.log('UNREGISTER') // #tokens-debugging

            if (!isEmpty(this.userInfos) && !isEmpty(this.userInfos.externalUserId)) {
                cachios
                    .post(`${this.userWebUrl}/accounts/${this.userInfos.externalUserId}/unregister`, { force: true })
                    .then(response => {
                        // console.log('UNREGISTER >>> SUCCESS') // #tokens-debugging

                        if (this.store.state.login.loginContext === 'partial' && !isEmpty(this.store.state.login.unregisterSyncUrl)) {
                            this.syncUnregisterAction()
                        } else {
                            emitter.emit('unregistered')
                        }
                    })
                    .catch(error => {
                        // console.log('UNREGISTER >>> ERROR', error) // #tokens-debugging
                        emitter.emit('unregisterError', error.response)
                        const event = new Event('kas-unregister-error', { detail: error.response })
                        document.body.dispatchEvent(event)
                    })
            } else {
                console.log('Unable to unregister : you have to provide an accountId')
            }
        }

        this.getConfigurationAction = (params) => {
            // console.log('GET_CONFIGURATION') // #tokens-debugging
            const getConfiguration = () => {
                // console.log('getConfiguration', this.retryCounter.configuration)
                this.retryCounter.configuration++
                return cachios
                    .get(`${this.userWebUrl}/configuration?redirectUrl=${this.redirectUrl}`, {
                        force: true,
                        headers: {
                            Authorization: ''
                        }
                    })
                    .then(response => {
                        // console.log('GET_CONFIGURATION >>> SUCCESS') // #tokens-debugging
                        this.retryCounter.configuration = 0
                        this.setLocalStorageKasConfiguration(response.data)

                        document.body.dispatchEvent(new CustomEvent('kas-configuration-loaded', {
                            detail: {
                                kasConfiguration: response.data
                            }
                        }))

                        if (typeof params !== 'undefined' && typeof params.callback !== 'undefined' && typeof params.callback === 'function') {
                            // console.log('GET_CONFIGURATION >>> SUCCESS >>> CALLBACK CALL !!!') // #tokens-debugging
                            params.callback()
                        }
                    })
                    .catch(error => {
                        // console.log('GET_CONFIGURATION >>> ERROR', error) // #tokens-debugging
                        emitter.emit('getConfigurationError', error.response)
                        const event = new Event('kas-get-configuration-error', { detail: error.response })
                        document.body.dispatchEvent(event)
                    })
            }

            if (this.retryCounter.configuration > 0) {
                const timeout = retryLatency ** this.retryCounter.configuration * 1000
                // console.log('Timeout for configuration will be ' + timeout / 1000 + 's') // #tokens-debugging
                const promise = new Promise((resolve, reject) => {
                    setTimeout(() => {
                        getConfiguration()
                    }, timeout)
                })
                return promise
            } else {
                return getConfiguration()
            }
        }

        this.getConfigurationSettingsAction = (params) => {
            // console.log('GET_CONFIGURATION_SETTINGS')
            const getConfigurationSettings = () => {
                // console.log('getConfigurationSettings', this.retryCounter.configurationSettings)
                this.retryCounter.configurationSettings++
                cachios
                    .get(`${this.userWebUrl}/configuration/settings`, {
                        force: true,
                        headers: {
                            Authorization: ''
                        }
                    })
                    .then(response => {
                        // console.log('GET_CONFIGURATION_SETTINGS >>> SUCCESS')
                        // console.log(response.data)
                        this.setLocalStorageKasConfigurationSettings(response.data)

                        if (response.data?.forms) {
                            this.setKasConfigurationSettingsFormsCookie(response.data.forms)
                        }

                        if (response.data?.pages) {
                            this.setKasConfigurationSettingsPagesCookie(response.data.pages)
                        }

                        this.store.commit(`login/${mutationTypes.SET_KAS_CONFIGURATION_SETTINGS}`, response.data)

                        document.body.dispatchEvent(new CustomEvent('kas-configuration-settings-loaded', {
                            detail: {
                                kasConfigurationSettings: response.data
                            }
                        }))

                        if (typeof params !== 'undefined' && typeof params.callback !== 'undefined' && typeof params.callback === 'function') {
                            params.callback()
                        }
                    })
                    .catch(error => {
                        // console.log('GET_CONFIGURATION_SETTINGS >>> ERROR', error)
                        emitter.emit('getConfigurationSettingsError', error.response)
                        const event = new Event('kas-get-configuration-settings-error', { detail: error.response })
                        document.body.dispatchEvent(event)
                    })
            }

            if (this.retryCounter.configurationSettings > 0) {
                setTimeout(() => {
                    if (isEmpty(this.kasConfigurationSettings)) {
                        getConfigurationSettings()
                    }
                }, retryLatency ** this.retryCounter.configurationSettings)
            } else {
                if (isEmpty(this.kasConfigurationSettings)) {
                    getConfigurationSettings()
                }
            }
        }

        this.getUserInfosAction = (params) => {
            // console.log('GET_USER_INFOS')
            return new Promise((resolve, reject) => {
                const getUserInfos = (params) => {
                    // console.log('getUserInfos', this.retryCounter.me, params) // #tokens-debugging
                    this.retryCounter.me++
                    cachios
                        .get(`${this.userWebUrl}/me?attributes=${this.userInfosAttributes}`, { force: true })
                        .then(response => {
                            // console.log('GET_USER_INFOS >>> SUCCESS')
                            // console.log(response.data.attributes)
                            this.retryCounter.me = 0

                            if (typeof params !== 'undefined' && typeof params.callback !== 'undefined' && typeof params.callback === 'function') {
                                // console.log('GET_USER_INFOS >>> callback') // #tokens-debugging
                                params.callback(response.data)
                            } else {
                                if (!isEmpty(params) && !isEmpty(params.checkAuthenticationStatus)) {
                                    // console.log('CHECK AUTHORISATION', response) // #tokens-debugging
                                    if (response.status === 401) {
                                        // console.log('CHECK AUTHORISATION >>> RESPONSE IS STILL 401, SO LOGOUT USER') // #tokens-debugging
                                        this.logoutAction({
                                            autoLogOut: true,
                                            callback: () => {
                                                emitter.emit('autoLoggedOut')
                                                const event = new Event('kas-logged-out')
                                                document.body.dispatchEvent(event)
                                            }
                                        })
                                    } else if (typeof params !== 'undefined' && typeof params.callback !== 'undefined' && typeof params.callback === 'function') {
                                        params.callback(response.data)
                                        // } else {
                                        // console.log('CHECK AUTHORISATION >>> RESPONSE IS NOT 401, SO IT WAS PROBABLY A BUG OR SOMETHING LIKE THAT :)') // #tokens-debugging
                                    }
                                } else if (response.data.attributes) {
                                    if (this.isAdditionalInfoRequired) {
                                        this.setUserInfosCookie(response.data)
                                        this.store.commit(`login/${mutationTypes.SET_USER_INFOS}`, this.userInfos)
                                        this.store.commit(`login/${mutationTypes.SET_RAW_USER_INFOS}`, this.rawUserInfos)
                                    }
                                    this.store.commit(`login/${mutationTypes.SET_AUTHENTICATED}`, true)
                                    // console.log('EMIT loggedIn IN GET_USER_INFOS >>> SUCCESS') // #tokens-debugging
                                    emitter.emit('loggedIn')

                                    document.body.dispatchEvent(new CustomEvent('kas-user-infos-loaded', {
                                        detail: {
                                            userInfos: response.data
                                        }
                                    }))
                                } else {
                                    // console.log('GET_USER_INFOS >>> BUT HALF SUCCESS') // #tokens-debugging
                                    this.getAccountsAction()
                                }
                            }
                            resolve(response.data)
                        })
                        .catch(error => {
                            // console.log('GET_USER_INFOS >>> ERROR', error)
                            emitter.emit('getUserInfosError', error?.response)
                            const event = new Event('kas-get-user-infos-error', { detail: error?.response })
                            document.body.dispatchEvent(event)

                            if (error?.response?.status !== 401) {
                                this.getAccountsAction()
                            }
                            reject(error)
                        })
                }

                if (this.retryCounter.me > 0) {
                    setTimeout(() => {
                        getUserInfos(params)
                    }, retryLatency ** this.retryCounter.me)
                } else {
                    getUserInfos(params)
                }
            })
        }

        this.getUserTokenAction = (params) => {
            // console.log('GET_USER_TOKEN') // #tokens-debugging
            // console.log('getUserTokenAction', i18n.locale)
            cachios
                .get(`${this.userWebUrl}/token?code=${params.code}&redirectUrl=${encodeURI(this.redirectUrl)}&loginMode=${this.loginMode}`, { force: true })
                .then(response => {
                    // console.log('GET_USER_TOKEN >>> SUCCESS') // #tokens-debugging
                    this.getUserTokenSuccessHandler(response.data)
                })
                .catch(error => {
                    // console.log('GET_USER_TOKEN >>> ERROR', error) // #tokens-debugging
                    // TODO : Manage retry
                    emitter.emit('invalidLoginCode', error.response)
                    const event = new Event('kas-get-user-token-error', { detail: error.response })
                    document.body.dispatchEvent(event)
                })
        }

        // UserIs (legacy) login
        this.loginAction = (params) => {
            // console.log('LOGIN')
            cachios
                .post(
                    `${this.userWebUrl}/login`,
                    {
                        username: params.data.username,
                        password: params.data.password
                    },
                    {
                        force: true
                    }
                )
                .then(response => {
                    // console.log('LOGIN >>> SUCCESS')
                    this.setUserTokenCookie(response.data)
                    this.applyUserTokenToAxios()
                    this.store.commit(`login/${mutationTypes.SET_USER_TOKEN}`, this.userToken)
                    emitter.emit('userTokenLoaded')
                    // console.log(this.userInfos)
                    this.store.commit(`login/${mutationTypes.SET_USER_INFOS}`, this.userInfos)
                    this.store.commit(`login/${mutationTypes.SET_RAW_USER_INFOS}`, this.rawUserInfos)

                    // console.log('EMIT loggedIn IN LOGIN ACTION')
                    emitter.emit('loggedIn')
                })
                .catch(error => {
                    // console.log('LOGIN >>> ERROR', error)
                    emitter.emit('loginError', error.response)
                    const event = new Event('user-is-login-error', { detail: error.response })
                    document.body.dispatchEvent(event)
                })
        }

        this.logoutAction = async (params) => {
            // console.log('LOGOUT') // #tokens-debugging
            let event = new Event('kas-logging-out')
            document.body.dispatchEvent(event)

            if (this.loginMode === 'kas') {
                const onKasLogoutParams = {}
                if (!isEmpty(params) && !isEmpty(params.autoLogOut)) {
                    onKasLogoutParams.autoLogOut = true
                }
                if (!isEmpty(this.userToken)) {
                    let grantType
                    if (!isEmpty(this.kasConfiguration)) {
                        grantType = this.kasConfiguration.grantType
                    } else {
                        await this.getConfigurationAction()
                        if (!isEmpty(this.kasConfiguration)) {
                            grantType = this.kasConfiguration.grantType
                        } else {
                            console.log('Unable to log out correctly...')
                            return false
                        }
                    }
                    const body = {
                        refreshToken: this.userToken.refreshToken,
                        grantType
                    }
                    cachios
                        .post(`${this.userWebUrl}/logout`, body, { force: true })
                        .then(response => {
                            // console.log('LOGOUT >>> KAS logout SUCCESS') // #tokens-debugging
                            this.onKasLogoutCallback(onKasLogoutParams)
                        })
                        .catch(error => {
                            // console.log('LOGOUT >>> ERROR', error) // #tokens-debugging
                            emitter.emit('logoutError', error.response)
                            const event = new Event('kas-logout-error', { detail: error.response })
                            document.body.dispatchEvent(event)
                            // TODO : Manage retry
                            this.onKasLogoutCallback(onKasLogoutParams)
                        })
                } else {
                    this.onKasLogoutCallback()
                }
            } else if (this.store.state.login.loginContext === 'partial' && !isEmpty(this.store.state.login.logoutSyncUrl)) {
                this.clearStoredInfos()
                this.syncLogoutAction()
                if (typeof params !== 'undefined' && typeof params.callback !== 'undefined' && typeof params.callback === 'function') {
                    params.callback()
                }
            } else {
                // console.log('NOT WIDGET CONTEXT')
                this.clearStoredInfos()
                event = new Event('kas-logged-out')
                document.body.dispatchEvent(event)
                emitter.emit('loggedOut')
                if (typeof params !== 'undefined' && typeof params.callback !== 'undefined' && typeof params.callback === 'function') {
                    params.callback()
                }
            }
        }

        this.refreshTokenAction = (params) => {
            // console.log('REFRESH_TOKEN') // #tokens-debugging
            delete cachios.axiosInstance.defaults.headers.common.Authorization
            // console.log('REFRESH_TOKEN >>> Body : userToken.refreshToken : ', ((!isEmpty(this.userToken) && !isEmpty(this.userToken.refreshToken)) ? this.userToken.refreshToken.substr(this.userToken.refreshToken.length - 10) : 'userToken SEEMS TO BE EMPTY'), this.userToken) // #tokens-debugging
            if (!isEmpty(this.userToken)) {
                cachios
                    .post(
                        `${this.userWebUrl}/refresh-token`,
                        {
                            refreshToken: this.userToken.refreshToken
                        },
                        {
                            // ttl: 0,
                            force: true
                        })
                    .then(response => {
                        // console.log('REFRESH_TOKEN >>> SUCCESS') // #tokens-debugging
                        // console.log(response.data) // #tokens-debugging
                        if (response.data) {
                            // console.log('REFRESH_TOKEN >>> TOKEN REFRESHED') // #tokens-debugging
                            // console.log('CHECK_ACCOUNT >>> REMOVE hasToRefreshToken FROM LOCALSTORAGE') // #tokens-debugging
                            localStorage.removeItem('hasToRefreshToken')
                            this.adaptTokenRefreshSafetyDelayIfNeeded(response.data)
                            this.setUserTokenCookie(response.data)
                            this.applyUserTokenToAxios()
                            this.store.commit(`login/${mutationTypes.SET_USER_TOKEN}`, this.userToken)
                            this.updateUserInfosCookieExpiration()
                            emitter.emit('userTokenRefreshed')
                            document.body.dispatchEvent(new CustomEvent('kas-token-refreshed', {
                                detail: {
                                    accessToken: this.userToken.accessToken
                                }
                            }))

                            if (typeof params !== 'undefined' && typeof params.callback !== 'undefined' && typeof params.callback === 'function') {
                                params.callback()
                            }
                        } else {
                            // console.log('REFRESH_TOKEN >>> TOKEN EXPIRED') // #tokens-debugging
                            emitter.emit('userTokenExpired')
                        }
                    })
                    .catch(error => {
                        // console.log('REFRESH_TOKEN >>> ERROR', error) // #tokens-debugging
                        emitter.emit('refreshTokenError', error.response)
                        const event = new Event('kas-refresh-token-error', { detail: error.response })
                        document.body.dispatchEvent(event)
                        // TODO : Manage retry
                        this.logoutAction()
                    })
            } else {
                // console.log('USER TOKEN IS EMPTY') // #tokens-debugging
            }

            // this.store.commit(`login/${mutationTypes.SET_USER_TOKEN_EXPIRATION}`, false)
        }

        this.syncLoginAction = (params) => {
            // console.log('SYNC_LOGIN') // #tokens-debugging
            delete cachios.axiosInstance.defaults.headers.common.Authorization
            cachios
                .get(this.store.state.login.loginSyncUrl, {
                    force: true,
                    withCredentials: true
                })
                .then(response => {
                    // console.log('SYNC_LOGIN >>> SUCCESS') // #tokens-debugging
                    this.applyUserTokenToAxios()
                    emitter.emit('syncLoginSuccess')
                })
                .catch(error => {
                    // console.log('SYNC_LOGIN >>> ERROR', error) // #tokens-debugging
                    this.applyUserTokenToAxios()
                    emitter.emit('syncLoginError', error.response)
                    const event = new Event('kas-sync-login-error', { detail: error.response })
                    document.body.dispatchEvent(event)
                })
        }

        this.syncLogoutAction = (params) => {
            // console.log('SYNC_LOGOUT') // #tokens-debugging
            cachios
                .get(this.store.state.login.logoutSyncUrl, {
                    force: true,
                    withCredentials: true
                })
                .then(response => {
                    // console.log('SYNC_LOGOUT >>> SUCCESS') // #tokens-debugging
                    // console.log('LOGGED OUT !!!!') // #tokens-debugging
                    emitter.emit('loggedOut')
                    const event = new Event('kas-logged-out')
                    document.body.dispatchEvent(event)
                })
                .catch(error => {
                    // console.log('SYNC_LOGOUT >>> ERROR', error) // #tokens-debugging
                    emitter.emit('syncLogoutError', error.response)
                    const event = new Event('kas-sync-logout-error', { detail: error.response })
                    document.body.dispatchEvent(event)
                })
        }

        this.syncUnregisterAction = (params) => {
            // console.log('SYNC_UNREGISTER') // #tokens-debugging
            cachios
                .get(this.store.state.login.unregisterSyncUrl, {
                    force: true,
                    withCredentials: true
                })
                .then(response => {
                    // console.log('SYNC_UNREGISTER >>> SUCCESS') // #tokens-debugging
                    emitter.emit('unregistered')
                })
                .catch(error => {
                    // console.log('SYNC_UNREGISTER >>> ERROR', error) // #tokens-debugging
                    emitter.emit('syncUnregisterError', error.response)
                    const event = new Event('kas-sync-unregister-error', { detail: error.response })
                    document.body.dispatchEvent(event)
                })
        }

        this.syncRefreshAction = (params) => {
            // console.log('SYNC_REFRESH') // #tokens-debugging

            delete cachios.axiosInstance.defaults.headers.common.Authorization
            cachios
                .get(this.store.state.login.refreshSyncUrl, {
                    force: true,
                    withCredentials: true
                })
                .then(response => {
                    // console.log('SYNC_REFRESH >>> SUCCESS') // #tokens-debugging
                    this.applyUserTokenToAxios()
                    emitter.emit('syncRefreshSuccess')
                })
                .catch(error => {
                    // console.log('SYNC_REFRESH >>> ERROR', error) // #tokens-debugging
                    this.applyUserTokenToAxios()
                    emitter.emit('syncRefreshError', error.response)
                    const event = new Event('kas-sync-refresh-error', { detail: error.response })
                    document.body.dispatchEvent(event)
                })
        }

        this.syncUserStatusAction = (params) => {
            // console.log('SYNC_USER_STATUS') // #tokens-debugging

            delete cachios.axiosInstance.defaults.headers.common.Authorization
            cachios
                .get(this.store.state.login.userStatusSyncUrl, {
                    force: true,
                    withCredentials: true
                })
                .then(response => {
                    // console.log('SYNC_USER_STATUS >>> SUCCESS') // #tokens-debugging
                    // console.log(response)
                    if (typeof response.data.isSynchronized !== 'undefined' && !response.data.isSynchronized) {
                        if (typeof response.data.userToken !== 'undefined') {
                            // On applique le userToken
                            // console.log('CALL getUserTokenSuccessHandler', response.data.userToken)
                            this.getUserTokenSuccessHandler(response.data.userToken)
                            // } else {
                            //     // On rafraîchit la page
                            //     // console.log('RELOAD PAGE')
                            //     // window.location.reload()
                        }
                        // } else {
                        // console.log('SYNCHRONIZED')
                    }
                    this.applyUserTokenToAxios()
                })
                .catch(error => {
                    // console.log('SYNC_USER_STATUS >>> ERROR', error) // #tokens-debugging
                    this.applyUserTokenToAxios()
                    emitter.emit('syncUserStatusError', error.response)
                    const event = new Event('kas-sync-user-status-error', { detail: error.response })
                    document.body.dispatchEvent(event)
                })
        }

        this.setEventHandlers = () => {
            emitter.on('validUserTokenFound', () => {
                // console.log('validUserTokenFound IN setEventHandlers') // #tokens-debugging
                this.applyUserTokenToAxios()
                // console.log('EMIT userTokenLoaded') // #tokens-debugging
                emitter.emit('userTokenLoaded')
                this.store.commit(`login/${mutationTypes.SET_USER_TOKEN}`, this.userToken)

                if (!isEmpty(this.userInfos)) {
                    this.store.commit(`login/${mutationTypes.SET_USER_INFOS}`, this.userInfos)
                    this.store.commit(`login/${mutationTypes.SET_RAW_USER_INFOS}`, this.rawUserInfos)
                    this.store.commit(`login/${mutationTypes.SET_AUTHENTICATED}`, true)
                    // console.log('EMIT loggedIn IN ON validUserTokenFound')
                    emitter.emit('loggedIn')
                }
            })

            emitter.on('userTokenExpired', () => {
                // console.log('userTokenExpired IN setEventHandlers') // #tokens-debugging
                // this.logoutAction()
                this.logoutAction(() => {
                    // console.log('startLogInProcess FROM userTokenExpired >>> logoutAction') // #tokens-debugging
                    this.startLogInProcess()
                })
            })
            emitter.on('loggedIn', () => {
                const event = new Event('kas-logged-in')
                document.body.dispatchEvent(event)
                if (this.loginMode && this.loginMode === 'kas' && isEmpty(this.kasConfigurationSettings)) {
                    this.getConfigurationSettingsAction()
                }
            })
            emitter.on('clientSideUserTokenValidated', () => {
                // console.log('ON clientSideUserTokenValidated EVENT')
                this.store.commit(`login/${mutationTypes.SET_USER_INFOS}`, this.userInfos)
                this.store.commit(`login/${mutationTypes.SET_RAW_USER_INFOS}`, this.rawUserInfos)
                this.setLocalStorageItem('userStatusSyncRetry', 1)
                window.location.reload()
            })
            // emitter.on('invalidLoginCode', () => {
            // console.log('invalidLoginCode IN INIT')
            // })
            // emitter.on('loggedIn', () => {
            // console.log('loggedIn IN INIT')
            // })
            // emitter.on('loggedOut', () => {
            // console.log('loggedOut IN INIT')
            // })
            // emitter.on('syncLoginSuccess', () => {
            // console.log('syncLoginSuccess IN INIT')
            // })
            // emitter.on('userInfosLoaded', () => {
            // console.log('userInfosLoaded IN INIT')
            // })
            // emitter.on('userTokenExpired', () => {
            // console.log('userTokenLoaded IN INIT')
            // })
            // emitter.on('userTokenLoaded', () => {
            // console.log('userTokenLoaded IN INIT')
            // })
            // emitter.on('userTokenRefreshed', () => {
            // console.log('userTokenRefreshed IN INIT')
            // })
        }

        this.init = () => {
            if (!this.isInitialized) {
                this.setEventHandlers()
            }
            this.isInitialized = true
        }

        this.loadConfig = () => {
            this.getConfigurationSettingsAction()
            this.getConfigurationAction()
        }

        // Initialize object
        return LoginManager.instance
    }

    /* =================================================================================================================
    =============================================== GETTERS AND SETTERS ================================================
    ================================================================================================================= */
    get apiKey () {
        return this._apiKey
    }

    set apiKey (apiKey) {
        this._apiKey = apiKey

        if (apiKey) {
            cachios.axiosInstance.defaults.headers.common['X-API-Key'] = apiKey

            return
        }

        delete cachios.axiosInstance.defaults.headers.common['X-API-Key']
    }

    get additionalInfoDomain () {
        return this._additionalInfoDomain
    }

    set additionalInfoDomain (additionalInfoDomain) {
        this._additionalInfoDomain = additionalInfoDomain
    }

    get checkAccount () {
        return this._checkAccount
    }

    set checkAccount (checkAccount) {
        this._checkAccount = checkAccount
    }

    get cookiesDomain () {
        return this._cookiesDomain
    }

    set cookiesDomain (cookiesDomain) {
        this._cookiesDomain = cookiesDomain
    }

    get isAdditionalInfoRequired () {
        return this._isAdditionalInfoRequired
    }

    set isAdditionalInfoRequired (isAdditionalInfoRequired) {
        this._isAdditionalInfoRequired = isAdditionalInfoRequired
    }

    get isInitialized () {
        return this._isInitialized
    }

    set isInitialized (isInitialized) {
        this._isInitialized = isInitialized
    }

    get isRefreshTokenEnabled () {
        return this._isRefreshTokenEnabled
    }

    set isRefreshTokenEnabled (isRefreshTokenEnabled) {
        this._isRefreshTokenEnabled = isRefreshTokenEnabled
    }

    get kasConfiguration () {
        return this._kasConfiguration
    }

    set kasConfiguration (kasConfiguration) {
        this._kasConfiguration = kasConfiguration
    }

    get kasConfigurationSettings () {
        return this._kasConfigurationSettings
    }

    set kasConfigurationSettings (kasConfigurationSettings) {
        this._kasConfigurationSettings = kasConfigurationSettings
    }

    get kasConfigurationSettingsForms () {
        return this._kasConfigurationSettingsForms
    }

    set kasConfigurationSettingsForms (kasConfigurationSettingsForms) {
        this._kasConfigurationSettingsForms = kasConfigurationSettingsForms
    }

    get kasConfigurationSettingsPages () {
        return this._kasConfigurationSettingsPages
    }

    set kasConfigurationSettingsPages (kasConfigurationSettingsPages) {
        this._kasConfigurationSettingsPages = kasConfigurationSettingsPages
    }

    get loginMode () {
        return this._loginMode
    }

    set loginMode (loginMode) {
        this._loginMode = loginMode
    }

    get rawUserInfos () {
        return this._rawUserInfos
    }

    set rawUserInfos (rawUserInfos) {
        this._rawUserInfos = rawUserInfos
    }

    get redirectUrl () {
        return this._redirectUrl
    }

    set redirectUrl (redirectUrl) {
        this._redirectUrl = redirectUrl
    }

    get retryCounter () {
        return this._retryCounter
    }

    set retryCounter (retryCounter) {
        this._retryCounter = retryCounter
    }

    get serverSideStatus () {
        return this._serverSideStatus
    }

    set serverSideStatus (serverSideStatus) {
        this._serverSideStatus = serverSideStatus
    }

    get store () {
        return this._store
    }

    set store (store) {
        this._store = store
    }

    get tokenRefreshSafetyDelay () {
        return this._tokenRefreshSafetyDelay
    }

    set tokenRefreshSafetyDelay (tokenRefreshSafetyDelay) {
        this._tokenRefreshSafetyDelay = tokenRefreshSafetyDelay
    }

    get userWebUrl () {
        return this._userWebUrl
    }

    set userWebUrl (userWebUrl) {
        this._userWebUrl = userWebUrl
    }

    get userInfos () {
        return this._userInfos
    }

    set userInfos (userInfos) {
        this._userInfos = userInfos
    }

    get userInfosAttributes () {
        return this._userInfosAttributes
    }

    set userInfosAttributes (userInfosAttributes) {
        this._userInfosAttributes = userInfosAttributes
    }

    get userToken () {
        return this._userToken
    }

    set userToken (userToken) {
        this._userToken = userToken
    }
}

const instance = new LoginManager()
Object.seal(instance)

export default instance
